import { useEffect, useState } from "react";
import type { Link, User } from "@dropin/shared/schemas";
import { type ClaimRow, listMyClaims, logout, updateClaim } from "../api";
import { CreatorProfileTab } from "./CreatorProfileTab";
import { BrowseNearby } from "./BrowseNearby";
import { LinksEditor } from "./LinksEditor";
import { MapIcon, MobileMenu, TicketIcon, UserIcon } from "./MobileMenu";

type Tab = "browse" | "profile" | "mine";

const TAB_LABEL: Record<Tab, string> = {
  browse: "Browse",
  profile: "Profile",
  mine: "My claims",
};

export function CreatorDashboard({
  user,
  onUserUpdated,
}: {
  user: User;
  onUserUpdated: (u: User) => void;
}) {
  // Verified creators land on Browse (their primary action); unverified
  // creators land on Profile so they finish their profile and request review.
  const [tab, setTab] = useState<Tab>(user.verified ? "browse" : "profile");
  const [claims, setClaims] = useState<ClaimRow[]>([]);
  const [loading, setLoading] = useState(true);

  async function refresh() {
    const c = await listMyClaims();
    setClaims(c.claims);
    setLoading(false);
  }
  useEffect(() => {
    if (tab === "mine") refresh();
  }, [tab]);

  return (
    <>
      {/* Mobile: section label + hamburger portal up into the Layout header. */}
      <MobileMenu
        currentLabel={TAB_LABEL[tab]}
        items={[
          {
            key: "browse",
            label: "Browse",
            icon: <MapIcon />,
            active: tab === "browse",
            onSelect: () => setTab("browse"),
            testid: "menu-browse",
          },
          {
            key: "profile",
            label: "Profile",
            icon: <UserIcon />,
            active: tab === "profile",
            onSelect: () => setTab("profile"),
            testid: "menu-profile",
          },
          {
            key: "mine",
            label: "My claims",
            icon: <TicketIcon />,
            active: tab === "mine",
            badge: claims.length || undefined,
            onSelect: () => setTab("mine"),
            testid: "menu-my-claims",
          },
        ]}
        onSignOut={() => logout().then(() => location.assign("/"))}
      />

      {/* Desktop: classic tab bar */}
      <div className="hidden sm:block">
        <div className="inline-flex rounded-md border border-border bg-paper p-1 sm:shadow-none">
          <TabBtn active={tab === "browse"} onClick={() => setTab("browse")} testid="tab-browse">
            Browse
          </TabBtn>
          <TabBtn active={tab === "profile"} onClick={() => setTab("profile")} testid="tab-profile">
            Profile
          </TabBtn>
          <TabBtn active={tab === "mine"} onClick={() => setTab("mine")} testid="tab-my-claims">
            My claims ({claims.length})
          </TabBtn>
        </div>
      </div>

      {tab === "browse" && <BrowseNearby />}
      {tab === "profile" && <CreatorProfileTab user={user} onUpdated={onUserUpdated} />}
      {tab === "mine" && <MyClaimsTab claims={claims} loading={loading} onChanged={refresh} />}
    </>
  );
}

function TabBtn({
  active,
  onClick,
  children,
  testid,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
  testid: string;
}) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`rounded px-3.5 py-1.5 text-sm font-semibold transition-colors ${
        active ? "bg-surface text-ink shadow-xs" : "text-muted hover:text-ink"
      }`}
    >
      {children}
    </button>
  );
}

function ClaimStatusChip({ status }: { status: string; testid?: string }) {
  const cls =
    status === "approved" || status === "fulfilled"
      ? "chip-success"
      : status === "rejected"
        ? "chip"
        : "chip-accent";
  return <span className={cls}>{status}</span>;
}

function MyClaimsTab({
  claims,
  loading,
  onChanged,
}: {
  claims: ClaimRow[];
  loading: boolean;
  onChanged: () => void;
}) {
  if (loading) return <div className="card text-muted">Loading…</div>;
  if (claims.length === 0) {
    return <div className="card text-muted">No claims yet — go grab a perk.</div>;
  }
  return (
    <div className="grid gap-3">
      {claims.map((c) => (
        <ClaimRowCard key={c.id} claim={c} onChanged={onChanged} />
      ))}
    </div>
  );
}

function ClaimRowCard({ claim, onChanged }: { claim: ClaimRow; onChanged: () => void }) {
  // Seed the editor with whatever's there: prefer the new contentLinks
  // array, fall back to wrapping the legacy contentUrl as a single link.
  const seedLinks: Link[] = (() => {
    if (claim.contentLinks && claim.contentLinks.length > 0) return claim.contentLinks;
    if (claim.contentUrl) return [{ label: "Post", url: claim.contentUrl }];
    return [];
  })();
  const [links, setLinks] = useState<Link[]>(seedLinks);
  const [note, setNote] = useState(claim.note ?? "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const showCode =
    (claim.status === "approved" || claim.status === "fulfilled") &&
    !!claim.perkRedemptionCode;
  // Pre-clean: drop rows that are missing either label or url.
  const cleanLinks = links
    .map((l) => ({ label: l.label.trim(), url: l.url.trim() }))
    .filter((l) => l.label && l.url);
  const submittedLinks: Link[] =
    claim.contentLinks && claim.contentLinks.length > 0
      ? claim.contentLinks
      : claim.contentUrl
        ? [{ label: "Post", url: claim.contentUrl }]
        : [];
  return (
    <div className="card" data-testid={`my-claim-${claim.id}`}>
      <div className="flex items-start gap-3">
        {claim.perkImageUrl && (
          <div className="shrink-0 h-14 w-14 rounded-md border border-border bg-paper overflow-hidden">
            <img
              src={claim.perkImageUrl}
              alt=""
              className="h-full w-full object-cover"
              onError={(e) => {
                (e.currentTarget as HTMLImageElement).style.display = "none";
              }}
            />
          </div>
        )}
        <div className="min-w-0 flex-1">
          <div data-testid={`claim-status-${claim.id}`} className="inline-flex">
            <ClaimStatusChip status={claim.status} />
          </div>
          <h4 className="mt-2 text-base font-semibold">{claim.perkTitle}</h4>
          <p className="text-sm text-muted">{claim.businessName}</p>
        </div>
      </div>

      {showCode && (
        <div
          className="mt-3 rounded-md border border-success/40 bg-success/[0.06] p-3"
          data-testid={`redemption-code-${claim.id}`}
        >
          <p className="text-xs font-semibold uppercase tracking-wider text-success">
            Show this to staff
          </p>
          <p className="mt-1 font-mono text-xl tracking-widest text-ink">
            {claim.perkRedemptionCode}
          </p>
        </div>
      )}

      {claim.status === "approved" && (
        <div className="mt-3 grid gap-3">
          <p className="text-xs text-muted">
            Post your content, drop one or more links below, and add a note if you want
            — this completes the claim and emails the business.
          </p>
          <div className="grid gap-1.5">
            <span className="text-xs font-semibold uppercase tracking-wider text-muted">
              Content links
            </span>
            <LinksEditor
              value={links}
              onChange={setLinks}
              testid={`claim-links-${claim.id}`}
            />
          </div>
          <label className="grid gap-1.5">
            <span className="text-xs font-semibold uppercase tracking-wider text-muted">
              Note (optional)
            </span>
            <textarea
              className="input text-sm min-h-[72px]"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Anything you want them to know — best viewing time, tagged accounts, etc."
              maxLength={2000}
              data-testid={`claim-note-${claim.id}`}
            />
          </label>
          {error && <p className="text-xs text-rose">{error}</p>}
          <div>
            <button
              disabled={saving || cleanLinks.length === 0}
              className="btn-accent text-xs"
              onClick={async () => {
                setSaving(true);
                setError(null);
                try {
                  await updateClaim(claim.id, {
                    contentLinks: cleanLinks,
                    note: note.trim() || null,
                  });
                  await onChanged();
                } catch (e) {
                  setError(e instanceof Error ? e.message : "failed");
                } finally {
                  setSaving(false);
                }
              }}
              data-testid={`submit-content-${claim.id}`}
            >
              {saving
                ? "Posting…"
                : cleanLinks.length === 0
                  ? "Add at least one link"
                  : `Submit ${cleanLinks.length} link${cleanLinks.length === 1 ? "" : "s"}`}
            </button>
          </div>
        </div>
      )}

      {claim.status === "fulfilled" && submittedLinks.length > 0 && (
        <div className="mt-3 rounded-md border border-border bg-paper p-3">
          <p className="text-xs font-semibold uppercase tracking-wider text-muted">
            What you submitted
          </p>
          <ul className="mt-2 grid gap-1">
            {submittedLinks.map((l, i) => (
              <li key={i} className="flex items-baseline gap-2">
                <span className="text-xs text-muted shrink-0">{l.label}</span>
                <a
                  href={l.url}
                  target="_blank"
                  rel="noreferrer"
                  className="text-sm truncate"
                >
                  {l.url} ↗
                </a>
              </li>
            ))}
          </ul>
          {claim.note && (
            <p className="mt-2 text-xs text-muted whitespace-pre-wrap leading-relaxed">
              <span className="font-semibold">Note:</span> {claim.note}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
